import { DocType, DOC_TYPE } from 'config'
import {
  CsvConfigColumnInvoiceOrderForm,
  CsvConfigColumnOcrBuilder,
} from 'types'
import { downloadFile } from 'utils/file'

import { get, post, put2 } from './fetch'

interface RowObjectInvoiceOrderForm {
  columns: CsvConfigColumnInvoiceOrderForm[]
}

export type CsvConfig =
  | CsvConfigInvoice
  | CsvConfigOrderForm
  | CsvConfigOcrBuilder
  | CsvConfigGeneralOcr

interface BaseCsvConfig {
  dialogDisp: boolean
  header: boolean
}

export interface CsvConfigInvoice extends BaseCsvConfig {
  invoice: CsvConfigInvoiceObj
}

export interface CsvConfigOrderForm extends BaseCsvConfig {
  orderForm: CsvConfigOrderFormObj
}

export interface CsvConfigOcrBuilder extends BaseCsvConfig {
  ocrBuilder: CsvConfigOcrBuilderObj
}

export interface CsvConfigGeneralOcr extends BaseCsvConfig {
  generalOCR: CsvConfigGeneralOcrObj
}

export interface CsvConfigGeneralOcrObj {
  zipExcel: boolean
  zipCsv: boolean
  zipText: boolean
  zipJson: boolean
}

export interface CsvConfigInvoiceObj {
  fileName: CsvConfigColumnInvoiceOrderForm
  headerBase: RowObjectInvoiceOrderForm
  headerBillFrom: RowObjectInvoiceOrderForm
  headerBillTo: RowObjectInvoiceOrderForm
  headerBank: RowObjectInvoiceOrderForm
  headerContact: RowObjectInvoiceOrderForm
  other: RowObjectInvoiceOrderForm
  itemLines: RowObjectInvoiceOrderForm
}

export interface CsvConfigOrderFormObj {
  fileName: CsvConfigColumnInvoiceOrderForm
  headerBase: RowObjectInvoiceOrderForm
  headerOrderer: RowObjectInvoiceOrderForm
  headerSupplier: RowObjectInvoiceOrderForm
  headerDelivery: RowObjectInvoiceOrderForm
  other: RowObjectInvoiceOrderForm
  itemLines: RowObjectInvoiceOrderForm
}

export interface CsvConfigOcrBuilderObj {
  csvByDocumentType?: boolean
  config: {
    no: number
    templateName: string
    templateCsvOrder: number
    columns: CsvConfigColumnOcrBuilder[]
  }[]
}

let CsvConfigCache: Record<
  string,
  | CsvConfigInvoice
  | CsvConfigOrderForm
  | CsvConfigGeneralOcr
  | CsvConfigOcrBuilder
  | null
> = {} // null: 特殊ケース

export const resetCache = () => {
  CsvConfigCache = {}
}

function mkCKey(t: DocType, s?: string, oId?: number) {
  return `${t}:${s || ''}:${oId || ''}`
}

/*
  get displayDialog flag from csv config
  and caches it's API result
*/
export async function getCSvConfigDialoDispAndCache(
  documentType: DocType,
  subGroup?: string,
  ocrConfigID?: number,
) {
  const params = {
    documentType,
    subGroup,
    ocrBuilderConfigID: ocrConfigID,
  }
  const cKey = mkCKey(documentType, subGroup, ocrConfigID)
  try {
    const cached = CsvConfigCache[cKey]
    if (cached === null) return true // 404のcase
    const data = cached || (await get('users/config/csv', { params }))
    CsvConfigCache[cKey] = data as any
  } catch (e: any) {
    if (
      documentType === DOC_TYPE.OCR_BUILDER &&
      e.response.status === HTTP_RCODE_NOT_FOUND
    ) {
      // old ocr builder type don't have config
      CsvConfigCache[cKey] = null
      return true
    }
    throw e
  }
  return CsvConfigCache[cKey]!.dialogDisp
}

export async function getCsvConfigInvoice(subGroup?: string) {
  return getCsvConfigInvoiceOrderFrom(
    DOC_TYPE.INVOICE,
    subGroup,
  ) as Promise<CsvConfigInvoice>
}

export async function getCsvConfigOrderForm(subGroup?: string) {
  return getCsvConfigInvoiceOrderFrom(
    DOC_TYPE.ORDER_FORM,
    subGroup,
  ) as Promise<CsvConfigOrderForm>
}

async function getCsvConfigInvoiceOrderFrom(
  documentType: typeof DOC_TYPE.INVOICE | typeof DOC_TYPE.ORDER_FORM,
  subGroup?: string,
) {
  const params = { documentType, subGroup }
  const cKey = mkCKey(documentType, subGroup)
  // TODO: any
  const data =
    CsvConfigCache[cKey] || (await get('users/config/csv', { params }))
  CsvConfigCache[cKey] = data as any
  return CsvConfigCache[cKey]
}

export async function getCsvConfigGeneralOcr() {
  const params = { documentType: DOC_TYPE.GENERAL_OCR }
  const cKey = mkCKey(DOC_TYPE.GENERAL_OCR)
  const data =
    CsvConfigCache[cKey] || (await get('users/config/csv', { params }))
  CsvConfigCache[cKey] = data as CsvConfigGeneralOcr
  return CsvConfigCache[cKey]
}

const HTTP_RCODE_NOT_FOUND = 404
export async function getCsvConfigOcrBuilder(
  configID: number,
  subGroup?: string,
) {
  const cKey = mkCKey(DOC_TYPE.OCR_BUILDER, subGroup, configID)
  const params = {
    documentType: DOC_TYPE.OCR_BUILDER,
    subGroup,
    ocrBuilderConfigID: configID,
  }
  try {
    const cached = CsvConfigCache[cKey]
    if (cached === null) return null
    const data = cached || (await get('users/config/csv', { params }))
    CsvConfigCache[cKey] = data as CsvConfigOcrBuilder
    return CsvConfigCache[cKey]
  } catch (e: any) {
    if (
      e.message === 'my magic key' ||
      e.response.status === HTTP_RCODE_NOT_FOUND
    ) {
      // old ocr builder type don't have config
      CsvConfigCache[cKey] = null
      return null
    }
    throw e
  }
}

export function saveCsvConfig(
  docType: DocType,
  subGroup: string | undefined, // verify
  ocrbuilderConfigID: number | undefined, // required if it's ocrbuilder
  payload: CsvConfig,
) {
  resetCache()
  switch (docType) {
    case DOC_TYPE.INVOICE:
      return saveCsvConfigInvoice(subGroup, payload as CsvConfigInvoice)
    case DOC_TYPE.ORDER_FORM:
      return saveCsvConfigOrderForm(subGroup, payload as CsvConfigOrderForm)
    case DOC_TYPE.OCR_BUILDER:
      return saveCsvConfigOcrBuilder(
        subGroup,
        ocrbuilderConfigID!,
        payload as CsvConfigOcrBuilder,
      )
    case DOC_TYPE.GENERAL_OCR:
      return saveCsvConfigGeneralOcr(subGroup, payload as CsvConfigGeneralOcr)
  }
  throw new Error('not recognized doc type')
}

function saveCsvConfigOrderForm(
  subGroup: string | undefined,
  payload: CsvConfig,
) {
  const urlAddition = subGroup ? `&subGroup=${subGroup}` : ''
  return put2(
    `users/config/csv?documentType=${DOC_TYPE.ORDER_FORM}${urlAddition}`,
    payload,
  )
}

function saveCsvConfigInvoice(
  subGroup: string | undefined,
  payload: CsvConfig,
) {
  const urlAddition = subGroup ? `&subGroup=${subGroup}` : ''
  return put2(
    `users/config/csv?documentType=${DOC_TYPE.INVOICE}${urlAddition}`,
    payload,
  )
}

function saveCsvConfigGeneralOcr(
  subGroup: string | undefined,
  payload: CsvConfig,
) {
  const urlAddition = subGroup ? `&subGroup=${subGroup}` : ''
  return put2(
    `users/config/csv?documentType=${DOC_TYPE.GENERAL_OCR}${urlAddition}`,
    payload,
  )
}

function saveCsvConfigOcrBuilder(
  subGroup: string | undefined,
  ocrBuilderConfigID: number,
  payload: CsvConfig,
) {
  const urlAddition = subGroup ? `&subGroup=${subGroup}` : ''
  return put2(
    `users/config/csv?documentType=${DOC_TYPE.OCR_BUILDER}&ocrBuilderConfigID=${ocrBuilderConfigID}${urlAddition}`,
    payload,
  )
}

function decodeFilename(encoded: string) {
  return window.decodeURIComponent(encoded.replace(/\+/g, ' '))
}

export const downloadCsvFile = async (objectIDs: string[]) => {
  const DEFAULT_FNAME = 'download-file'
  const downloadResponse = await post('documents/csv', { objectIDs }, true) // true: file can be zip
  const { data, headers } = downloadResponse
  const encodedFilename =
    headers['content-disposition'].match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    )![1] || DEFAULT_FNAME
  const filename = decodeFilename(encodedFilename)
  downloadFile(
    filename,
    filename.match(/\.zip$/) ? 'application/zip' : 'text/csv',
    data,
  )
}

export const downloadLogCsvFile = async (payload: {
  header: boolean
  isZip: boolean
}) => {
  const DEFAULT_FNAME = 'csv-file'
  const downloadResponse = await post('log/csv', payload, payload.isZip)
  const { data, headers } = downloadResponse
  const encodedFilename =
    headers['content-disposition'].match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    )![1] || DEFAULT_FNAME
  const filename = decodeFilename(encodedFilename)
  downloadFile(
    filename,
    filename.match(/\.zip$/) ? 'application/zip' : 'text/csv',
    data,
  )
}
